
import {
  CheckForPlanAdjustmentsResult,
  GenerateScheduleResult,
  PlanUpdateOutcome,
  PlanUpdateParams,
  PlanUpdateResults,
  PlanUpdateSolved,
  PlanUpdateUserMessage,
} from './plan-update-interface';
import { Schedule_ItemType, Schedule_ItemDayStart, Schedule_Day, Schedule_Item } from './data-model/schedule';
import { logEntryHoldsUserData } from './data-model/log-entry-user-data';
import { INFOODS, KCAL_PER_G_PROTEIN, KCAL_PER_G_FAT, KCAL_PER_G_CARBS, KCAL_PER_G_FIBER, getMinimumDailyFat_g, getMinimumDailyCarbs_g, splitRemainingKcalByCarbRatio } from './data-model/food';
import { Gender, BodyCompositionType, ActivityLevel, DedicationLevel } from './enums';
import {
  PlanUpdateInternals,
  BodyFatSeries,
  DatedSlopePoint,
  AdjustmentAction,
  AdjustmentActionReason,
  WeighInAnalysis,
} from './plan-update-internals';
import { averageTrainingLevels, getTrainingLevels } from './data-model/training-level';
import { FitnessPlan } from './data-model/fitness-plan';
import { deepCheckValid } from './deep-helpers';
import { isUsersTrainingGoalGrowth, MacroTargetsType, usesCalorieCycling } from './data-model/user-state';
import { DateTime } from 'luxon';
import {
  slopeEstimationPercentile,
  interceptEstimationPercentile,
  averageValidNumbers,
} from './shared-util';
import { constraintIntakeDeltaPercentForDedicationLevel } from './intake-delta';
import { getLocalOffsetTimeInDesiredTimeZone } from './time';
import { Log_Meal } from './data-model/log-meal';
import { getAverageWaist } from './data-model/log-weigh-in';
import { estimateWeightFromSamples, MIN_BODY_FAT_SAMPLES_FOR_TREND, MIN_WEIGHINS_LAST_7D_FOR_TREND, MIN_WEIGHINS_LAST_14D_FOR_TREND } from './weight-trend';
import { getEntryLocalTimeInDesiredTimeZone, LogRecordType, LogRecordState, Log_Entry } from './data-model/user-log';
import { sumBy } from 'lodash-es';
import { trainingStatusTable, physicalActivityTable, findEnergyBalanceFactor, estimateBFandLBM, findBodyComposition } from './plan-update-shared';
import { describeError } from './error-describe';

// #2855 Phase 1: minimum samples required in the 14-day fatness window before the
// "unclear -> adjust" cutting branches in findAdjustmentAction will fire an intake
// cut. Caliper is cleaner per sample so it gets the lower bar; tape is noisier so
// it gets the higher bar. See design-docs/body-comp-measurement-cadence.md for the
// underlying analysis. Exported so unit tests can pin the threshold without
// drift; no other file consumes these at runtime.
export const MIN_CALIPER_SAMPLES_FOR_CUT = 5;
export const MIN_TAPE_SAMPLES_FOR_CUT = 7;
// the fewest body-fat readings for a trend lives beside the weight rule in weight-trend.ts, so
// the charts of #5456 apply the same number; exported from here too for the tests that pin it
export { MIN_BODY_FAT_SAMPLES_FOR_TREND };

/**
 * The calendar day of the earliest reading the progress check fitted (#5456): the earliest of the
 * weight readings and of the body-fat series it chose. Undefined when it fitted no weight trend,
 * because then it judged nothing. `statsStart` is NOT this day: it is only a lower bound, and the
 * check also drops readings older than 14 days and keeps at most 14 per series.
 */
export function firstCountedDayIso(analysis: Readonly<WeighInAnalysis>): string | undefined {
  if (analysis.weightSlope_kgwk === undefined) {
    return undefined;
  }
  // the readings of the body-fat series the check chose, if it chose one
  let fatSamples: readonly DatedSlopePoint[] = [];
  switch (analysis.fatnessSeries) {
    case BodyFatSeries.Calipers:
      fatSamples = analysis.caliperSamples;
      break;
    case BodyFatSeries.Tape:
      fatSamples = analysis.tapeSamples;
      break;
  }
  let earliest: string | undefined;
  // for each fitted reading, keep the earliest day (ISO dates sort as text)
  for (const sample of [...analysis.weightSamples, ...fatSamples]) {
    if (sample.dateIso && (earliest === undefined || sample.dateIso < earliest)) {
      earliest = sample.dateIso;
    }
  }
  return earliest;
}

export class PlanUpdate {
  params: PlanUpdateParams;
  schedule: Schedule_Day[];
  fitnessPlan: FitnessPlan;
  internals: PlanUpdateInternals = {};
  message: PlanUpdateUserMessage;
  today_utc: DateTime;
  today_local: DateTime;
  statsStart: DateTime; /** we only account for measurement data that is not older than last nutrition update */
  warnings: string = '';
  /** set when adjustForProgress healed a goal-inconsistent energy balance — see PlanUpdateSolved.healedIntakeDelta (#4037) */
  healedIntakeDelta: PlanUpdateSolved['healedIntakeDelta'] = undefined;
  /** set when this solve's decrease was raised back up to the minimum daily intake — see PlanUpdateSolved.clampedToMinimumIntake (#5415) */
  clampedToMinimumIntake: boolean = undefined;
  /** set when findAdjustmentAction needed the body-fat trend and had too few readings to judge it — see CheckForPlanAdjustmentsSolved.needsMoreBodyFatReadings (#6067) */
  waitingOnBodyFat: boolean = false;

  private prepareGeneralParams($: PlanUpdateInternals, params: PlanUpdateParams) {
    this.params = params;
    this.schedule = params.schedule;
    this.fitnessPlan = params.fitnessPlan;
    this.message = PlanUpdateUserMessage.AllOk;
    this.waitingOnBodyFat = false;

    $.gender = this.params.immutableState.gender;
    if ($.gender === null) {
      throw new Error('Invalid gender');
    }

    // determine training status
    let level = averageTrainingLevels(Object.values(getTrainingLevels(params.declaredState.declaredStrength, params.fitnessPlan)));
    $.trainingStatus = trainingStatusTable.find(s => s.level === level);
    if ($.trainingStatus === null) {
      throw new Error('Invalid training status');
    }

    this.today_utc = DateTime.fromISO(params.today_local);
    this.today_local = DateTime.fromISO(params.today_local, { setZone: true });

    if (!this.today_utc.isValid) {
      throw new Error('Invalid current time');
    }

    $.mealsDaily = this.params.declaredState.mealFrequency;
    if ($.mealsDaily === null) {
      throw new Error('Invalid meals per day');
    }
    $.trainingDaysWeekly = sumBy(params.declaredState.trainingDays, t => (t ? 1 : 0));
    $.bulkMealsPerWeek = $.trainingStatus.anabolicMeals * $.trainingDaysWeekly;
    $.bulkMealsPerWeek = Math.min($.bulkMealsPerWeek, $.mealsDaily * 7);
    $.cutMealsPerWeek = 7 * $.mealsDaily - $.bulkMealsPerWeek;
  }

  /**
   * Analyzes recent weigh-in data and establishes current weight estimate, and eventual fat loss/gain slope.
   * Also sets the message to the user for whether the weigh-in measurements are often and consistent enough.
   * Called only by runAnalysisStage, which adds the body-fat estimate to the result.
   */
  private analyzeWeightIns(): WeighInAnalysis {
    let params = this.params;
    let startPointDate = DateTime.fromISO(params.fitnessPlan.startPoint.timestamp_utc);
    let nutritionChangeDate =
      params.fitnessPlan.nutritionAdjusted !== undefined && params.fitnessPlan.nutritionAdjusted.timestamp_utc !== undefined
        ? DateTime.fromISO(params.fitnessPlan.nutritionAdjusted.timestamp_utc)
        : this.today_utc.minus({ days: 14 });

    this.statsStart = startPointDate > nutritionChangeDate ? startPointDate : nutritionChangeDate;

    // filter data for the last two weeks and convert to format used by the slope estimator
    let weightSamples: DatedSlopePoint[] = [];
    let tapeSamples: DatedSlopePoint[] = [];
    let caliperSamples: DatedSlopePoint[] = [];
    let usedOldTapes = false; // TODO Remove this after a month!
    for (let sample of this.params.weighIns) {
      const sampleTime = DateTime.fromISO(sample.time_utc);
      const sampleTimeLocal = getEntryLocalTimeInDesiredTimeZone(sample, DateTime.fromISO(this.params.today_local));
      if (sampleTime < this.statsStart && !sampleTimeLocal.hasSame(getLocalOffsetTimeInDesiredTimeZone(this.statsStart, sample.timeOffset_min, sampleTimeLocal), 'day')) {
        continue; // ignore all data older than last nutrition change
      }
      // day relative to today (will be <=0 since samples are in the past)
      let day = Math.round(sampleTime.diff(this.today_utc).as('days'));
      if (day < -14 || day > 0) {
        continue;
      }
      // the entry's own calendar day, for screens that draw the readings the check fitted (#5456)
      const dateIso = sampleTimeLocal.toISODate();
      if (sample.weight_kg) {
        weightSamples.push({ x: day, y: sample.weight_kg, dateIso });
      }
      if (sample.tapeMeasures) {
        if (sample.tapeMeasures.waistMin_cm || sample.tapeMeasures.waistMax_cm) {
          usedOldTapes = true;
        } else {
          let t = getAverageWaist(sample);
          if (!isNaN(t) && t > 10 && t < 200) {
            tapeSamples.push({ x: day, y: t, dateIso });
          }
        }
      }
      if (sample.caliperMeasures) {
        let sites = this.params.immutableState.gender === Gender.male ? [sample.caliperMeasures.suprailiac_mm, sample.caliperMeasures.umbilical_mm] : [sample.caliperMeasures.suprailiac_mm, sample.caliperMeasures.umbilical_mm, sample.caliperMeasures.thigh_mm];
        let c = 0;
        for (let s of sites) {
          c += averageValidNumbers(s, 0);
        }
        c /= sites.length;
        if (!isNaN(c) && c > 3 && c < 1000) {
          caliperSamples.push({ x: day, y: c, dateIso });
        }
      }
    }
    // if old tapes were used at any point - we forfeit the entire se of tapes data
    // this is because we can't mix the old method with the new one
    if (usedOldTapes) {
      tapeSamples = [];
    }

    // make sure we use just last 14 measurements.
    if (weightSamples.length > 14) {
      weightSamples = weightSamples.slice(-14);
    }
    if (tapeSamples.length > 14) {
      tapeSamples = tapeSamples.slice(-14);
    }
    if (caliperSamples.length > 14) {
      caliperSamples = caliperSamples.slice(-14);
    }

    // If there were enough weigh-ins *within the last week*, we consider the weight data "good".
    /*
    NOTE: we only check the last week for consistency, as we don't want to nag the user for things that happened so far behind.
    We still account for all data in the calculations.
    */
    let optimalWeights = weightSamples.filter(s => s.x > -7).length >= MIN_WEIGHINS_LAST_7D_FOR_TREND;

    // the estimates start out missing and are filled in below when the data allows
    const analysis: WeighInAnalysis = {
      estimatedBW_kg: undefined,
      weightSlope_kgwk: undefined,
      fatnessSlopeBottom: undefined,
      fatnessSlopeTop: undefined,
      weightSamples,
      caliperSamples,
      tapeSamples,
    };

    // If there were enough weigh-ins *within the last _two_ weeks*, we consider the weight data "minimal".
    // For this, the user is warned, but calculations are still carried out.
    let minimalWeights = weightSamples.length >= MIN_WEIGHINS_LAST_14D_FOR_TREND;
    let enoughWeights = optimalWeights || minimalWeights;

    // body fat is judged over the whole window, not the last week: the weigh-in page tells a
    // steady measurer to take a reading only every 5 days, which gives 3 readings in 14 days
    // but at most 2 in 7. Asking for 3 in the last week labelled every verdict of a user who
    // followed that rhythm "measure more often" (#6067). Whether there are enough readings to
    // cut calories on an unclear trend is a separate, stricter check in findAdjustmentAction.
    let enoughCalipers = caliperSamples.length >= MIN_BODY_FAT_SAMPLES_FOR_TREND;
    let enoughTapes = tapeSamples.length >= MIN_BODY_FAT_SAMPLES_FOR_TREND;

    // Calculate weight slope if we have enough weight measurements
    if (enoughWeights) {
      // only the weigh-ins decide AllOk vs MeasureMoreOften here; body-fat readings no longer
      // downgrade it (#6067)
      this.message = optimalWeights ? PlanUpdateUserMessage.AllOk : PlanUpdateUserMessage.MeasureMoreOften;

      // use shared Theil-Sen estimation function
      const estimation = estimateWeightFromSamples(weightSamples);
      analysis.estimatedBW_kg = estimation.estimatedBW_kg;
      analysis.weightSlope_kgwk = estimation.weightSlope_kgwk;

      // still need intercepts for erratic data check below
      let weightSlopeM = slopeEstimationPercentile(weightSamples, 0.5);
      let weightInterceptB = interceptEstimationPercentile(weightSamples, weightSlopeM, 0.3);
      let weightInterceptT = interceptEstimationPercentile(weightSamples, weightSlopeM, 0.7);

      // pick the series the trend is read from: a series with 3+ readings in the last week
      // first (calipers before tape), then calipers, then tape. This order decides calorie
      // changes, so #6067 kept it and only stopped labelling the 14-day case "measure more
      // often" -- a user measuring tape daily and calipers every 5 days still gets the tape trend
      const recentCalipers = caliperSamples.filter(s => s.x > -7).length >= MIN_BODY_FAT_SAMPLES_FOR_TREND;
      const recentTapes = tapeSamples.filter(s => s.x > -7).length >= MIN_BODY_FAT_SAMPLES_FOR_TREND;
      if (recentCalipers) {
        analysis.fatnessSlopeBottom = slopeEstimationPercentile(caliperSamples, 0.3);
        analysis.fatnessSlopeTop = slopeEstimationPercentile(caliperSamples, 0.7);
        analysis.fatnessSeries = BodyFatSeries.Calipers;
      } else if (recentTapes) {
        analysis.fatnessSlopeBottom = slopeEstimationPercentile(tapeSamples, 0.3);
        analysis.fatnessSlopeTop = slopeEstimationPercentile(tapeSamples, 0.7);
        analysis.fatnessSeries = BodyFatSeries.Tape;
      } else if (enoughCalipers) {
        analysis.fatnessSlopeBottom = slopeEstimationPercentile(caliperSamples, 0.3);
        analysis.fatnessSlopeTop = slopeEstimationPercentile(caliperSamples, 0.7);
        analysis.fatnessSeries = BodyFatSeries.Calipers;
      } else if (enoughTapes) {
        analysis.fatnessSlopeBottom = slopeEstimationPercentile(tapeSamples, 0.3);
        analysis.fatnessSlopeTop = slopeEstimationPercentile(tapeSamples, 0.7);
        analysis.fatnessSeries = BodyFatSeries.Tape;
      }

      /*
      Weight is considered "erratic" if the intercepts of confidence ranges are different by more than 2 kg. 
      This would mean that the estimated weight 7 days ago would also have a confidence interval wider than 2 kg.

      Fatness slope is considered erratic if the confidence interval spans 0, and smaller one is more 
      than 50% bigger one in absolute values. (Note the simplified logic because bigger one is always bigger 
      than 50% of smaller one anyway.)
       */
      let weightErratic = Math.abs(weightInterceptT - weightInterceptB) > 2;
      let fatnessErratic = analysis.fatnessSlopeBottom !== undefined && analysis.fatnessSlopeTop !== undefined &&
        (analysis.fatnessSlopeBottom * analysis.fatnessSlopeTop < 0 &&
          Math.abs(analysis.fatnessSlopeBottom) > Math.abs(analysis.fatnessSlopeTop / 2) &&
          Math.abs(analysis.fatnessSlopeTop) > Math.abs(analysis.fatnessSlopeBottom / 2));
      
      if (weightErratic || fatnessErratic) {
        this.message = PlanUpdateUserMessage.DataErratic;
      }

      // check weight for "flatness": flag the user if 3 or more weigh-ins in a row
      // are exactly identical (real scales almost always show some jitter, so a
      // long flat run usually means the scale is stuck or not being used).
      // start with no previous weight remembered yet, so the very first sample
      // is never compared against itself -- #4372 fixed a bug where seeding
      // lastWeight from the first sample double-counted it, firing this message
      // after only 2 identical readings instead of the intended 3+.
      let recentWeekWeights = weightSamples.filter(s => s.x > -7).map(s => s.y);
      let lastWeight: number | undefined = undefined;
      let identicalInARowCount = 0;
      for (let w of recentWeekWeights) {
        if (w === lastWeight) {
          identicalInARowCount++;
          if (identicalInARowCount >= 3) {
            this.message = PlanUpdateUserMessage.WeightTooFlat;
            break;
          }
        } else {
          lastWeight = w;
          identicalInARowCount = 1;
        }
      }

      // if there was not enough measurements
    } else {
      // just take the latest weight known
      let lastDay = -15;
      let lastWeight = undefined;
      for (let sample of weightSamples) {
        if (sample.x > lastDay) {
          lastDay = sample.x;
          lastWeight = sample.y;
        }
      }
      analysis.estimatedBW_kg = lastWeight;
      if (analysis.estimatedBW_kg === undefined) {
        analysis.estimatedBW_kg = this.params.fitnessPlan.nutritionAdjusted && this.params.fitnessPlan.nutritionAdjusted.BW_kg;
      }
      if (analysis.estimatedBW_kg === undefined) {
        analysis.estimatedBW_kg = this.params.fitnessState.weight_kg;
      }
      // last-resort fallback: the plan's startPoint baseline. set on plan generation
      // from fitnessState.weight_kg of the day, so it survives a later wipe of
      // fitnessState.weight_kg (Bug A pre-#3286). undefined for users whose entire
      // intake never carried a weight.
      if (analysis.estimatedBW_kg === undefined) {
        analysis.estimatedBW_kg = this.params.fitnessPlan.startPoint?.BW_kg;
      }

      // No bodyweight from any source — leave estimatedBW_kg undefined and signal
      // the UI to ask the user for a fresh weigh-in. The throw used to fire here
      // (Sentry CYBERNETIC-FITNESS-APP-369) silently broke "Save & Update" for
      // affected users (#3286). Downstream computeMaintenanceIntake also returns
      // undefined for missing weight, mirroring the missing-BF% safety net added
      // in 45b70d78f.
      if (analysis.estimatedBW_kg === undefined) {
        this.message = PlanUpdateUserMessage.LogWeighInNeeded;
        return analysis;
      }

      let statsSpan = this.today_utc.diff(startPointDate).as('days');

      // don't bother the user if there was not enough time for them to collect enough data yet,
      // otherwise complain
      if (statsSpan <= 7) {
        this.message = PlanUpdateUserMessage.AllOk;
      } else {
        this.message = PlanUpdateUserMessage.NotEnoughData;
      }
    }
    return analysis;
  }

  /**
   * Runs the whole analysis stage — the weigh-in analysis, then the body-fat estimate and the
   * body-composition tier built from it — and returns what it found. Also copies the result into
   * `this.internals`, which is the debug record handed back to tests and checked by deepCheckValid.
   */
  private runAnalysisStage(): Readonly<WeighInAnalysis> {
    const $ = this.internals;
    // this establishes the bodyweight estimate and the fat loss/gain slopes.
    // in the initial phases, it will just use the BW data from intake page
    const analysis = this.analyzeWeightIns();
    // copy each value into the debug record as soon as it exists, so that if a later step
    // throws, the entry points' catch blocks still report what was already known
    $.estimatedBW_kg = analysis.estimatedBW_kg;
    $.weightSlope_kgwk = analysis.weightSlope_kgwk;
    $.fatnessSlopeBottom = analysis.fatnessSlopeBottom;
    $.fatnessSlopeTop = analysis.fatnessSlopeTop;
    $.weightSamples = analysis.weightSamples;
    $.caliperSamples = analysis.caliperSamples;
    $.tapeSamples = analysis.tapeSamples;

    // estimates the body-fat percent from the start point and the estimated bodyweight.
    // in the initial phases, it will just use the BF data from intake page
    analysis.estimatedBF_pc = estimateBFandLBM(this.fitnessPlan, this.params.fitnessState, analysis.estimatedBW_kg);
    $.estimatedBF_pc = analysis.estimatedBF_pc;
    analysis.bodyComposition = findBodyComposition($.gender, analysis.estimatedBF_pc);
    $.bodyComposition = analysis.bodyComposition;
    return analysis;
  }

  private isBaselineComplete(): boolean {
    if (this.fitnessPlan.nominalDailyAverageIntake > 0 && this.fitnessPlan.nutritionAdjusted && this.fitnessPlan.nutritionAdjusted.BF_pc) {
      return true;
    } else {
      return false;
    }
  }

  computeMaintenanceIntake(analysis: Readonly<WeighInAnalysis>): number | undefined {

    let $ = this.internals;
    let params = this.params;

    // No bodyweight (analyzeWeightIns set LogWeighInNeeded) — return undefined
    // so initializeBaseline takes its early-return path and the user sees a
    // friendly message instead of a crash. Mirrors the missing-BF% guard.
    if (analysis.estimatedBW_kg === undefined) {
      return undefined;
    }
    // numeric-but-invalid weight is data corruption — keep loud.
    if (!isFinite(analysis.estimatedBW_kg) || analysis.estimatedBW_kg < 20 || analysis.estimatedBW_kg > 500) {
      throw new Error('Invalid bodyweight');
    }

    // if activityLevel is missing (e.g. stale intakeFormState from an incomplete new-mode session
    // was reused by a change-mode session without reloading all fields from declaredState)
    const activityLevel = this.params.declaredState.activityLevel ?? ActivityLevel.SomewhatActive;
    $.physicalActivityFactor = physicalActivityTable[$.gender][activityLevel];
    if (isNaN($.physicalActivityFactor)) {
      throw new Error('Invalid physical activity factor');
    }

    $.energyBalanceFactor = findEnergyBalanceFactor(params.declaredState, params.fitnessState, $.gender, analysis.bodyComposition, $.trainingStatus, analysis.estimatedBF_pc);

    // determine actual days and sets from training program
    let setsInPlan =
      // for all workouts in plan of sum of ...
      sumBy(params.fitnessPlan.workouts, wo =>
        // number of sets in that workout
        sumBy(wo.subSlotIds, subSlotId => params.fitnessPlan.subSlots[subSlotId].defaultVolume_sets),
      );
    let daysInPlan = params.fitnessPlan.workouts.length;
    $.setsWeekly = daysInPlan === 0 ? 0 : (setsInPlan / daysInPlan) * $.trainingDaysWeekly;

    let maintenanceIntake: number = undefined;
    if (analysis.estimatedBF_pc) {
      if (analysis.bodyComposition.type === BodyCompositionType.Obese) {

        let age = - DateTime.fromISO(this.params.immutableState.born).diffNow().as('years');
        $.BMR = 10 * analysis.estimatedBW_kg + 6.25 * this.params.immutableState.height_cm - 5 * age + (this.params.immutableState.gender === Gender.male ? 5 : -161);
      } else {
        // basic energy calculations
        let estimatedLBM_kg = analysis.estimatedBW_kg * (1 - analysis.estimatedBF_pc / 100);
        $.BMR = 370 + estimatedLBM_kg * 21.6;
      }
      $.restingDayEE = $.BMR * analysis.bodyComposition.TEF * $.physicalActivityFactor;
      $.resistanceTrainingEE = $.setsWeekly === 0 ? 0 : ($.setsWeekly / $.trainingDaysWeekly) * 3 * 0.1 * analysis.estimatedBW_kg;
      $.trainingDayEE = $.restingDayEE + (analysis.bodyComposition.TEF * $.resistanceTrainingEE);
      maintenanceIntake = ($.trainingDayEE * $.trainingDaysWeekly + $.restingDayEE * (7 - $.trainingDaysWeekly)) / 7;
    }

    return maintenanceIntake;
  }

  computeDailyAverageIntake(analysis: Readonly<WeighInAnalysis>, maintenanceIntake: number, minimumDailyAverageIntake: number): number {
    let $ = this.internals;
    if (this.params.declaredState.intakeDeltaPercent !== undefined) {
      return Math.max(minimumDailyAverageIntake, maintenanceIntake + this.params.declaredState.intakeDeltaPercent / 100 * maintenanceIntake);
    } else {
      if (analysis.bodyComposition.type === BodyCompositionType.Obese &&
        !isUsersTrainingGoalGrowth(this.params.declaredState.trainingGoalGrowth, $.gender, this.params.fitnessState.bfData.bf_pc)) {
        return Math.max(9.7 * 1.8 * (analysis.estimatedBW_kg + 2), maintenanceIntake * $.energyBalanceFactor);
      } else {
        return Math.max(minimumDailyAverageIntake, maintenanceIntake * $.energyBalanceFactor);
      }
    }
  }

  computeMinimumDailyAverageIntake(analysis: Readonly<WeighInAnalysis>, maintenanceIntake: number, intakeDeltaPercent?: number): number {
    let minimumDailyAverageIntake = 1000000;
    if (intakeDeltaPercent !== undefined) {
      minimumDailyAverageIntake = intakeDeltaPercent / 100 * maintenanceIntake + maintenanceIntake;
    }
    if (analysis.bodyComposition) {
      minimumDailyAverageIntake = Math.min(analysis.bodyComposition.factorM * 1.8 * analysis.estimatedBW_kg, minimumDailyAverageIntake);
    } else {
      minimumDailyAverageIntake = Math.min(11 * 1.8 * analysis.estimatedBW_kg, minimumDailyAverageIntake);
    }

    return minimumDailyAverageIntake;
  }

  private initializeBaseline(analysis: Readonly<WeighInAnalysis>) {
    let maintenanceIntake = this.computeMaintenanceIntake(analysis);

    // if no BF% data available (e.g. old plan with obsolete visual BF method),
    // computeMaintenanceIntake returns undefined — store weight snapshot but leave
    // baseline incomplete so it will be re-initialized when BF% is provided
    if (maintenanceIntake === undefined) {
      this.fitnessPlan.nutritionAdjusted = {
        timestamp_utc: this.today_utc.toISO(),
        BW_kg: analysis.estimatedBW_kg,
        BF_pc: analysis.estimatedBF_pc,
      };
      delete this.fitnessPlan.shouldInitializeBaselineAtNextReplan;
      return;
    }

    let minimumDailyAverageIntake = this.computeMinimumDailyAverageIntake(analysis, maintenanceIntake, this.params.declaredState.intakeDeltaPercent);

    let dailyAverageIntake: number = this.computeDailyAverageIntake(analysis, maintenanceIntake, minimumDailyAverageIntake);
    let intakeDeltaPercent = this.params.declaredState.intakeDeltaPercent !== undefined ? this.params.declaredState.intakeDeltaPercent / 100 : (dailyAverageIntake - maintenanceIntake) / maintenanceIntake;

    this.fitnessPlan.nominalDailyAverageIntake = dailyAverageIntake;
    this.fitnessPlan.nutritionAdjusted = {
      timestamp_utc: this.today_utc.toISO(),
      BW_kg: analysis.estimatedBW_kg,
      BF_pc: analysis.estimatedBF_pc,
    };
    delete this.fitnessPlan.shouldInitializeBaselineAtNextReplan;
    this.fitnessPlan.intakeDeltaPercent = intakeDeltaPercent;
    this.fitnessPlan.needsReadjustment = undefined;
    // the target just computed replaced whatever was in force, including any
    // number the user had typed, so no manual override is in force any more.
    // drop the recommendation remembered from before that override and clear the
    // flag saying the target came from the user. leaving them stale made the
    // edit-nutrition screen show a recommendation from before the program
    // setting changed (#5170).
    delete this.fitnessPlan.baselineNominalDailyAverageIntake;
    this.fitnessPlan.wasOverriddenManually = false;
  }

  private getMaxWeightLoss_kgwk(analysis: Readonly<WeighInAnalysis>): number {
    return (analysis.estimatedBW_kg * analysis.bodyComposition.weeklyWeightLoss_pc) / 100;
  }

  private getMinWeightLoss_kgwk(analysis: Readonly<WeighInAnalysis>): number {
    return (analysis.estimatedBW_kg * 0.3) / 2 / 100; // 0.3 percent per 2 weeks
  }

  private getMaxWeightGain_kgwk(analysis: Readonly<WeighInAnalysis>): number {
    let $ = this.internals;
    return (analysis.estimatedBW_kg * $.trainingStatus.weeklyWeightGain_pc) / 100;
  }

  private canAdjustPlan(analysis: Readonly<WeighInAnalysis>): boolean {
    // for users that still don't have BF determined, we can't do any adjustments yet anyway
    if (analysis.bodyComposition === undefined) {
      return false;
    }

    // allow overriding, regardless of measurements
    if (this.fitnessPlan.overrideDailyAverageIntake !== undefined) {
      return true;
    }

    // if weight slope is undefined, we can't make any adjustments
    if (analysis.weightSlope_kgwk === undefined) {
      return false;
    }

    return true;
  }

  private findAdjustmentAction(analysis: Readonly<WeighInAnalysis>) {
    let $ = this.internals;
    if (!this.canAdjustPlan(analysis)) {
      return;
    }

    $.adjustmentAction = undefined;
    $.adjustmentActionReason = undefined;

    // not that for obese, maximum weight loss is unlimited - the weeklyWeightLoss_pc is 100
    let maxWeightLoss_kgwk = this.getMaxWeightLoss_kgwk(analysis);
    let minWeightLoss_kgwk = this.getMinWeightLoss_kgwk(analysis);
    let maxWeightGain_kgwk = this.getMaxWeightGain_kgwk(analysis);
    if (maxWeightLoss_kgwk < minWeightLoss_kgwk) {
      throw new Error(`Weight loss min/max are reversed: min=${minWeightLoss_kgwk}, max=${maxWeightLoss_kgwk}`);
    }

    let adjustmentAction = undefined;
    let adjustmentActionReason = undefined;

    // #2855 Phase 1: the two cutting branches below that intake-cut on an "unclear"
    // fatness slope are unreliable when the underlying sample is thin (~3 points at
    // the current 5-day cadence). Defer those cuts unless we cross the per-modality
    // minimum count in the 14-day window. See
    // design-docs/body-comp-measurement-cadence.md.
    const hasEnoughFatnessSamples =
      analysis.caliperSamples.length >= MIN_CALIPER_SAMPLES_FOR_CUT ||
      analysis.tapeSamples.length >= MIN_TAPE_SAMPLES_FOR_CUT;
    // the verdict below needs the body-fat trend, and there are too few readings to judge it:
    // record that, so the weigh-in page asks for a reading today (#6067)
    const waitForBodyFatReadings = () => {
      this.waitingOnBodyFat = true;
    };
    // an unclear trend on too few readings defers the cut. Only override an upstream "info"
    // message; preserve more-actionable signals (DataErratic, WeightTooFlat, LogWeighInNeeded)
    // set by analyzeWeightIns. The override also accepts MeasureMoreOften, which the weight
    // check sets when weigh-ins are only "minimal", because the body-fat reason is the one
    // the user can act on to unblock the verdict.
    const deferCutForBodyFatReadings = () => {
      waitForBodyFatReadings();
      if (this.message === PlanUpdateUserMessage.AllOk ||
          this.message === PlanUpdateUserMessage.MeasureMoreOften) {
        this.message = PlanUpdateUserMessage.InsufficientFatnessConfidence;
      }
    };

    const bIsBulking = isUsersTrainingGoalGrowth(this.params.declaredState.trainingGoalGrowth, $.gender, this.params.fitnessState.bfData.bf_pc);
    
    // the same test applyPendingIntakeOverride uses, so the two cannot disagree. If this
    // picked ManualOverride for a value that method then refuses — a stored zero or a
    // negative — the plan would take the manual-override branch on every solve while the
    // number was never applied, which silently stops all progress adjustments for good.
    if (this.fitnessPlan.overrideDailyAverageIntake > 0) {
      adjustmentAction = AdjustmentAction.ManualOverride;
      // if cutting
    } else if (!bIsBulking) {
      // if losing too much weight (no matter the thinning or not)
      if (analysis.weightSlope_kgwk < -maxWeightLoss_kgwk) {
        adjustmentAction = AdjustmentAction.IncreaseIntakeByExcessLoss;
        adjustmentActionReason = AdjustmentActionReason.TooMuchWeightLoss;

        // if losing between minimum and maximum
      } else if (analysis.weightSlope_kgwk < -minWeightLoss_kgwk) {
        // Need fatness data to decide - return early if not available
        if (analysis.fatnessSlopeBottom === undefined) {
          waitForBodyFatReadings();
          return;
        }
        // if significantly fattening
        if (analysis.fatnessSlopeBottom > 0) {
          adjustmentAction = AdjustmentAction.DecreaseIntakeByFullDeficit;
          adjustmentActionReason = AdjustmentActionReason.TooMuchFatGain;
        } else {
          // if thinning, or at least probably not fattening
          adjustmentAction = AdjustmentAction.DoNothing;
        }

        // if losing weight but very slowly
      } else if (analysis.weightSlope_kgwk < 0) {
        // Need fatness data to decide - return early if not available
        if (analysis.fatnessSlopeTop === undefined || analysis.fatnessSlopeBottom === undefined) {
          waitForBodyFatReadings();
          return;
        }
        // if significantly thinning
        if (analysis.fatnessSlopeTop < 0) {
          adjustmentAction = AdjustmentAction.DoNothing;
        } else if (analysis.fatnessSlopeBottom > 0) {
          // if significantly fattening
          adjustmentAction = AdjustmentAction.DecreaseIntakeByFullDeficit;
          adjustmentActionReason = AdjustmentActionReason.TooMuchFatGain;
        } else if (hasEnoughFatnessSamples) {
          // if not significantly thinning, and the sample is dense enough to trust
          adjustmentAction = AdjustmentAction.DecreaseIntakeByHalfDeficit;
          adjustmentActionReason = AdjustmentActionReason.NotEnoughFatLoss;
        } else {
          // if unclear fatness slope on too few samples, defer the cut and prompt the user
          adjustmentAction = AdjustmentAction.DoNothing;
          deferCutForBodyFatReadings();
        }

        // if still gaining weight
      } else {
        // Need fatness data to decide - return early if not available.
        // We need bottom too: the significantly-fattening sub-case below uses it
        // (mirrors the losing-slowly branch shape, see #2855 review).
        if (analysis.fatnessSlopeTop === undefined || analysis.fatnessSlopeBottom === undefined) {
          waitForBodyFatReadings();
          return;
        }
        // if significantly thinning
        if (analysis.fatnessSlopeTop < 0) {
          adjustmentAction = AdjustmentAction.DoNothing;
        } else if (analysis.fatnessSlopeBottom > 0) {
          // if significantly fattening, fire the cut regardless of sample count;
          // a clear directional signal does not need the min-n gate.
          adjustmentAction = AdjustmentAction.DecreaseIntakeByFullDeficit;
          adjustmentActionReason = AdjustmentActionReason.TooMuchFatGain;
        } else if (hasEnoughFatnessSamples) {
          // if unclear and the sample is dense enough to trust, fire the cut
          adjustmentAction = AdjustmentAction.DecreaseIntakeByFullDeficit;
          adjustmentActionReason = AdjustmentActionReason.TooMuchFatGain;
        } else {
          // if unclear fatness slope on too few samples, defer the cut and prompt the user
          adjustmentAction = AdjustmentAction.DoNothing;
          deferCutForBodyFatReadings();
        }
      }

      // if bulking
    } else {
      // if not gaining weight
      if (analysis.weightSlope_kgwk <= 0) {
        adjustmentAction = AdjustmentAction.IncreaseIntakeByFullSurplus;
        adjustmentActionReason = AdjustmentActionReason.NotEnoughWeightGain;
        // if gaining weight as planned
      } else if (analysis.weightSlope_kgwk <= maxWeightGain_kgwk) {
        adjustmentAction = AdjustmentAction.DoNothing;
        // if gaining weight too quickly
      } else {
        // Need fatness data to decide - return early if not available
        if (analysis.fatnessSlopeBottom === undefined) {
          waitForBodyFatReadings();
          return;
        }
        // if significantly fattening
        if (analysis.fatnessSlopeBottom > 0) {
          adjustmentAction = AdjustmentAction.DecreaseIntakeByExcessGain;
          adjustmentActionReason = AdjustmentActionReason.TooMuchFatGain;
        } else {
          // if thinning, or at least probably not fattening
          adjustmentAction = AdjustmentAction.DoNothing;
        }
      }
    }

    const bAdjustmentInOppositeDirectionThanLast = this.fitnessPlan.nutritionAdjusted && this.fitnessPlan.nutritionAdjusted.lastAdjustmentDirection && this.fitnessPlan.nutritionAdjusted.lastAdjustmentDirection !== this.adjustmentActionToDirection(adjustmentAction);
    const nutritionAdjustedTimeLocal = DateTime.fromISO(this.fitnessPlan.nutritionAdjusted.timestamp_utc).setZone(this.today_local.zone);
    const timeSinceLastNutritionAdjustment = this.today_local.startOf('day').diff(nutritionAdjustedTimeLocal.startOf('day')).as('days');
    if (adjustmentAction === AdjustmentAction.DoNothing || adjustmentAction === AdjustmentAction.ManualOverride || !bAdjustmentInOppositeDirectionThanLast || timeSinceLastNutritionAdjustment >= 13) {
      $.adjustmentAction = adjustmentAction;
      $.adjustmentActionReason = adjustmentActionReason;
    } else {
      $.adjustmentAction = AdjustmentAction.DoNothing;
    }
  }

  /**
   * Throws away a parked daily calorie target that nobody could ever eat to.
   *
   * The Edit Daily Nutrition Goal screen lets a user type `0`, and that zero is parked on
   * the plan as a request in `overrideDailyAverageIntake` like any other typed number.
   * applyPendingIntakeOverride then refuses to put it into force — rightly, since a target
   * of zero is not something anybody can eat to — but it used to leave the request sitting
   * on the plan for good. Every screen showing the daily target prefers the request over
   * the real target with `??`, which passes a zero straight through, so the app went on
   * showing a daily target of 0 kcal for ever while the plan and its meal targets used a
   * different number entirely.
   *
   * So a request that is not a usable target is discarded rather than kept. This runs
   * early in solve(), before anything in THAT solve reads the field, so canAdjustPlan,
   * findAdjustmentAction and applyPendingIntakeOverride all see the same cleaned state and
   * cannot disagree about a zero. checkForPlanAdjustments is a second entry point and does
   * not call this — it only reads the plan and returns, saving nothing, so a zero it sees
   * survives no longer than until the next solve.
   *
   * A request the solver simply cannot apply YET — a positive number on a solve that
   * produced no recommendation of its own — is a different thing and is left alone here;
   * applyPendingIntakeOverride parks that one deliberately for the next solve.
   */
  private discardUnusableIntakeRequest() {
    const typedTarget = this.fitnessPlan.overrideDailyAverageIntake;
    // if a request is parked and it is not a number anybody can eat to
    if (typedTarget !== undefined && !(typedTarget > 0)) {
      delete this.fitnessPlan.overrideDailyAverageIntake;
    }
  }

  /**
   * Puts a daily calorie target the user typed into force, if one is still waiting.
   *
   * Typing a target does not set the plan's target directly. It parks the number on
   * the plan in `overrideDailyAverageIntake` — a request — and asks the solver to run.
   * This method is the only place that turns such a request into the real target
   * (`nominalDailyAverageIntake`), and solve() calls it on every path that has body data to
   * work from, so no branch of the solver silently drops a typed target any more (#5181).
   * It is not a promise that every solve applies one. The call site sits inside the solve's
   * body-data check, and the guard below on the solver having a recommendation of its own
   * leaves a request parked too. A request left parked either way is applied by a later solve,
   * not lost. A request the solver could never use — zero or negative — is a different matter:
   * discardUnusableIntakeRequest deletes it at the top of solve(), so it is neither parked nor
   * ever applied.
   *
   * Returns true when it put a target into force, so the caller can freeze the progress
   * anchor that goes with the new target. Returns false when it left the request parked.
   */
  private applyPendingIntakeOverride(): boolean {
    const typedTarget = this.fitnessPlan.overrideDailyAverageIntake;
    // if the user has not typed a target, or one was already applied earlier in this solve.
    // a stored zero or a negative number is not a target anybody can eat to, so it is left
    // parked and never put into force — which is what the only earlier consumer of this
    // field did too, by testing the number for truthiness.
    if (!(typedTarget > 0)) {
      return false;
    }
    // if the solver has no recommendation of its own on this solve, leave the request
    // parked: there would be nothing for the typed number to replace and nothing to
    // remember beside it. The caller decides this — see the call site in solve().
    if (!(this.fitnessPlan.nominalDailyAverageIntake > 0)) {
      return false;
    }
    // save the solver-computed nominal before the user override replaces it
    if (this.fitnessPlan.baselineNominalDailyAverageIntake == null) {
      this.fitnessPlan.baselineNominalDailyAverageIntake = this.fitnessPlan.nominalDailyAverageIntake;
    }
    this.fitnessPlan.nominalDailyAverageIntake = typedTarget;
    this.fitnessPlan.wasOverriddenManually = true;
    delete this.fitnessPlan.overrideDailyAverageIntake;
    // if the user reset back to the baseline, clear it
    if (Math.round(this.fitnessPlan.nominalDailyAverageIntake) === Math.round(this.fitnessPlan.baselineNominalDailyAverageIntake)) {
      delete this.fitnessPlan.baselineNominalDailyAverageIntake;
    }
    return true;
  }

  /** See section "Adjusting the nutrition plan" of the specs
   * ( https://docs.google.com/document/d/1x_mHsNWH1crist2ceWrf58ao7gauzobW6Fa-OTRwlqI/edit?pli=1#heading=h.3whwml4 )
   */
  private adjustForProgress(analysis: Readonly<WeighInAnalysis>) {
    let $ = this.internals;

    if (!this.canAdjustPlan(analysis)) {
      return;
    }

    this.findAdjustmentAction(analysis);

    // Early return if no adjustment action was determined (e.g., due to missing fatness data)
    if ($.adjustmentAction === undefined) {
      return;
    }

    if (
      isNaN(this.fitnessPlan.nominalDailyAverageIntake) ||
      this.fitnessPlan.nominalDailyAverageIntake <= 0
    ) {
      throw new Error('Invalid current calories');
    }

    // Use the user's CURRENT declared surplus/deficit, not the value frozen at
    // baseline init. A user who switched from a cutting baseline to a muscle-growth
    // goal would otherwise carry a stale negative delta here, which made the
    // IncreaseIntakeByFullSurplus branch throw "Invalid surplus" and hang the
    // weekly-checkup spinner (#3836). Mirror initializeBaseline's own formula and
    // refresh the stored value so the later minimum-intake calc (findNutritionTargets,
    // which reads fitnessPlan.intakeDeltaPercent) and the stored plan doc both heal
    // on this solve. For users whose plan is not stale the cached value already
    // equals the declared-derived one, so this is a no-op for them.
    // NOTE: the DECLARED value itself can also contradict the goal direction (a
    // cutting-era deficit carried across a goal switch, #4037) — that case is
    // healed per-branch below via healGoalInconsistentIntakeDelta.
    let intakeDeltaPercent =
      this.params.declaredState.intakeDeltaPercent !== undefined
        ? this.params.declaredState.intakeDeltaPercent / 100
        : this.fitnessPlan.intakeDeltaPercent;
    this.fitnessPlan.intakeDeltaPercent = intakeDeltaPercent;
    // adjust according to the action
    this.fitnessPlan.wasOverriddenManually = false;
    switch ($.adjustmentAction) {
      case AdjustmentAction.DoNothing:
        break;
      case AdjustmentAction.ManualOverride:
        this.applyPendingIntakeOverride();
        break;
      case AdjustmentAction.DecreaseIntakeByExcessGain:
        this.fitnessPlan.nominalDailyAverageIntake -= ((analysis.weightSlope_kgwk - this.getMaxWeightGain_kgwk(analysis)) * 8260) / 14;
        break;
      case AdjustmentAction.DecreaseIntakeByHalfDeficit:
        if (intakeDeltaPercent >= 0) {
          intakeDeltaPercent = this.healGoalInconsistentIntakeDelta(intakeDeltaPercent, $.adjustmentAction, this.getDefaultCuttingIntakeDelta(analysis));
        }
        this.fitnessPlan.nominalDailyAverageIntake += this.fitnessPlan.actualDailyAverageIntake * intakeDeltaPercent * 0.5;
        break;
      case AdjustmentAction.DecreaseIntakeByFullDeficit:
        if (intakeDeltaPercent >= 0) {
          intakeDeltaPercent = this.healGoalInconsistentIntakeDelta(intakeDeltaPercent, $.adjustmentAction, this.getDefaultCuttingIntakeDelta(analysis));
        }
        this.fitnessPlan.nominalDailyAverageIntake += this.fitnessPlan.actualDailyAverageIntake * intakeDeltaPercent;
        break;
      case AdjustmentAction.IncreaseIntakeByExcessLoss:
        // note: weight loss is >0 while weight slope is <0
        // note that we are operating on actual, not nominal here! This handles the case where
        this.fitnessPlan.actualDailyAverageIntake += ((-analysis.weightSlope_kgwk - this.getMaxWeightLoss_kgwk(analysis)) * 1816) / 14;
        this.fitnessPlan.nominalDailyAverageIntake = this.fitnessPlan.actualDailyAverageIntake;
        break;
      case AdjustmentAction.IncreaseIntakeByFullSurplus:
        if (intakeDeltaPercent <= 0) {
          intakeDeltaPercent = this.healGoalInconsistentIntakeDelta(intakeDeltaPercent, $.adjustmentAction, this.getDefaultBulkingIntakeDelta());
        }
        this.fitnessPlan.nominalDailyAverageIntake += this.fitnessPlan.actualDailyAverageIntake * intakeDeltaPercent;
        break;
      default:
        throw new Error(`Invalid adjustment action: ${$.adjustmentAction}`);
    }

    // if any action was taken
    if ($.adjustmentAction !== AdjustmentAction.DoNothing) {
      // we need to freeze the new reference values
      this.fitnessPlan.nutritionAdjusted = {
        timestamp_utc: this.today_utc.toISO(),
        BW_kg: analysis.estimatedBW_kg,
        BF_pc: analysis.estimatedBF_pc,
        lastAdjustmentDirection: this.adjustmentActionToDirection($.adjustmentAction),
      };
    }
    this.fitnessPlan.needsReadjustment = undefined;
  }

  private adjustmentActionToDirection(adjustmentAction: AdjustmentAction) {
    switch (adjustmentAction) {
      case AdjustmentAction.DecreaseIntakeByExcessGain:
      case AdjustmentAction.DecreaseIntakeByFullDeficit:
      case AdjustmentAction.DecreaseIntakeByHalfDeficit:
        return -1;
      case AdjustmentAction.IncreaseIntakeByExcessLoss:
      case AdjustmentAction.IncreaseIntakeByFullSurplus:
        return 1;
      default:
        return 0;
    }
  }

  /** the default bulking surplus the intake form would suggest for this user
   *  (energy-balance factor by training level, scaled by their dedication level) */
  private getDefaultBulkingIntakeDelta(): number {
    let $ = this.internals;
    const dedication = this.params.declaredState.nutritionDedicationLevel ?? DedicationLevel.Balanced;
    return constraintIntakeDeltaPercentForDedicationLevel($.trainingStatus.bulkingEBF - 1, dedication);
  }

  /** the default cutting deficit the intake form would suggest for this user
   *  (body-composition tier deficit, scaled by their dedication level).
   *  mirrors findEnergyBalanceFactor's `1 - (deficit_pc || 100) / 100`. */
  private getDefaultCuttingIntakeDelta(analysis: Readonly<WeighInAnalysis>): number {
    const dedication = this.params.declaredState.nutritionDedicationLevel ?? DedicationLevel.Balanced;
    return constraintIntakeDeltaPercentForDedicationLevel(-(analysis.bodyComposition.deficit_pc || 100) / 100, dedication);
  }

  /**
   * The stored energy balance contradicts the direction the adjustment needs
   * (e.g. a cutting-era deficit carried into a muscle-growth program across a
   * goal switch, #4037; or a deliberate 0 "maintain" while not gaining). The
   * old behavior was to throw, which failed the WHOLE solve and permanently
   * broke the weekly checkup for the affected user. Instead, substitute the
   * goal-consistent default delta the intake form itself would have suggested,
   * persist it on the plan, and record the substitution on the result so the
   * client can report it to telemetry (shared code has no Sentry runtime).
   *
   * DELIBERATE: the 0 ("maintain") case is healed too, even though 0 is a legal
   * slider position that the intake form's restore guard keeps
   * (isIntakeDeltaCompatibleWithProgram treats 0 as restorable). The two
   * components answer two different questions: the form asks "is this value
   * representable in the program?" (0 is), this branch asks "can I compute the
   * needed increase/decrease from it?" (from 0 it cannot — the adjustment would
   * be a no-op while the analysis says the user is off target). The substituted
   * default only changes the RECOMMENDATION the checkup shows; the user still
   * accepts or declines it. The canary's `stored` value (0 vs a wrong-sign
   * number) keeps the two cohorts distinguishable in Sentry.
   */
  private healGoalInconsistentIntakeDelta(stored: number, action: AdjustmentAction, defaultDelta: number): number {
    this.healedIntakeDelta = { stored, used: defaultDelta, action };
    this.fitnessPlan.intakeDeltaPercent = defaultDelta;
    return defaultDelta;
  }

  /**
   * Whether `findAccumulatedBulkMealsCount` counts this log as before "today". The range walk asks
   * this too, so a workout on the range's first day is counted by exactly one of the two halves.
   *
   * It compares instants, not ISO strings. `time_utc` ends in Z while `today_utc` carries the
   * device's own offset, so comparing the strings letter by letter moved the cut-off by that offset
   * on every device not on UTC (df-608d8575e147).
   */
  private isHistoryLog(log: Log_Entry): boolean {
    return DateTime.fromISO(log.time_utc) < this.today_utc;
  }

  private findAccumulatedBulkMealsCount() {
    let $ = this.internals;
    let countedWorkouts = 0;
    let workoutLogs = this.params.historyLogs.filter(log => log.type === LogRecordType.Workout && this.isHistoryLog(log));
    // a workout earns bulk meals unless the user skipped it, done or not. findCutBulkMeals counts
    // the schedule's workouts by that same rule, and the two must agree: "today" here is the first
    // day of whatever range the backlog rebuild covers, so a different rule here made the same meal
    // bulk or cut depending on where that range started (#6068)
    for (let iWorkout = workoutLogs.length - 1; iWorkout >= 0; --iWorkout) {
      if (workoutLogs[iWorkout].state !== LogRecordState.Skipped) {
        ++countedWorkouts;
      }
    }

    let bulkMeals = 0;
    let firstWorkout = workoutLogs[0];
    if (firstWorkout) {
      // if there are no workouts, assume no bulk meals
      let mealLogs = this.params.historyLogs.filter(
        log => log.type === LogRecordType.Meal && this.isHistoryLog(log) && log.time_utc > firstWorkout.time_utc,
      );
      for (let iMeal = mealLogs.length - 1; iMeal >= 0; --iMeal) {
        if (!(mealLogs[iMeal] as Log_Meal).cutMeal) {
          ++bulkMeals;
        }
      }
    }

    $.accumulatedBulkMealsCount = Math.max(countedWorkouts * $.trainingStatus.anabolicMeals - bulkMeals, 0);
  }

  /**
   * The local hours of the workouts stored on `dayDate` that the user did not skip and that hold
   * something the user entered (Done, in progress, or holding a finished set).
   *
   * These are the workouts the rebuild can never delete, so once the day is before the range they
   * are exactly what `findAccumulatedBulkMealsCount` counts. An empty Scheduled placeholder is left
   * out on purpose: on a future day it is stale whenever the rebuild runs because the rules just
   * changed, and counting it would give bulk meals to a workout the user removed.
   *
   * On the first day, a workout the history count already counts is left out too. Both ask
   * `isHistoryLog`, so the two halves cannot both count it, or both miss it.
   */
  private findKeptWorkoutHours(dayDate: DateTime, isFirstDay: boolean): number[] {
    const dayStart = dayDate.startOf('day');
    const hours: number[] = [];
    for (const e of this.params.historyLogs) {
      if (e.type !== LogRecordType.Workout || e.state === LogRecordState.Skipped || !logEntryHoldsUserData(e)) {
        continue;
      }
      const entryTime = getEntryLocalTimeInDesiredTimeZone(e, dayDate);
      if (!dayDate.hasSame(entryTime, 'day') || (isFirstDay && this.isHistoryLog(e))) {
        continue;
      }
      hours.push(entryTime.diff(dayStart, 'hours').hours);
    }
    return hours;
  }

  // run through the week (TWICE!) and mark which meals are cut and which are bulk...
  /* NOTE: We need to mark bulk meals that can be as much as a day or two after a workout - hence "twice". Just in case.
   */
  private findCutBulkMeals() {
    this.findAccumulatedBulkMealsCount();

    let $ = this.internals;
    let accumulatedBulkMealsCount = $.accumulatedBulkMealsCount;
    for (let iDay = 0; iDay < this.schedule.length; ++iDay) {
      let day = this.schedule[iDay];
      // We use this variable to detect skipped workouts on first day of the schedule generation,
      // schedule item does not know about "skipping" info.
      const dayDate = this.today_local.plus({ days: iDay });
      const skippedWorkout =
        this.params.historyLogs.find(
          e =>
            e.type === LogRecordType.Workout &&
            e.state === LogRecordState.Skipped &&
            dayDate.hasSame(getEntryLocalTimeInDesiredTimeZone(e, dayDate), 'day'),
        ) !== undefined;
      const dayItemsSorted = day.items.sort((a, b) => a.time_hr - b.time_hr);
      const scheduledWorkouts = dayItemsSorted.filter(item => item.type === Schedule_ItemType.Workout && !skippedWorkout).length;
      const keptWorkoutHours = this.findKeptWorkoutHours(dayDate, iDay === 0);
      // if the day keeps more workouts the user did than the rules schedule, those workouts are what
      // the history count will see once the day has passed, so they earn the bulk meals, at their own
      // times, in place of the scheduled one. nothing is added to day.items: a workout item there
      // would make the rebuild create a log
      const keptWorkoutsWin = keptWorkoutHours.length > scheduledWorkouts;
      const walk: { time_hr: number; item?: Schedule_Item }[] = dayItemsSorted
        .filter(item => !keptWorkoutsWin || item.type !== Schedule_ItemType.Workout)
        .map(item => ({ time_hr: item.time_hr, item }));
      if (keptWorkoutsWin) {
        walk.push(...keptWorkoutHours.map(time_hr => ({ time_hr })));
        // a kept workout goes before a meal at the very same time, as a scheduled one does: the
        // rules list the workout item ahead of the meals
        walk.sort((a, b) => a.time_hr - b.time_hr || (a.item ? 1 : 0) - (b.item ? 1 : 0));
      }
      for (const step of walk) {
        const item = step.item;
        if (!item || (item.type === Schedule_ItemType.Workout && !skippedWorkout)) {
          accumulatedBulkMealsCount += $.trainingStatus.anabolicMeals;
        } else if (item.type === Schedule_ItemType.Meal) {
          // We do not want to take into consideration meals which are before start time of the schedule
          // For example when skipping workouts and adjusting meals after workout we don't want to change
          // possible meals before in the same day.
          let itemTime = this.today_local.startOf('day').plus({ hours: item.time_hr });
          if (iDay === 0 && itemTime < this.today_local) {
            continue;
          }
          if (accumulatedBulkMealsCount > 0) {
            item.cutMeal = false;
            accumulatedBulkMealsCount--;
          } else {
            item.cutMeal = true;
          }
        }
      }
    }
  }

  /**
   * Called just before the minimum daily intake raises a target that fell below it.
   *
   * A clamp only matters to the weekly checkup when it undoes a cut this solve made, so this
   * records `clampedToMinimumIntake` for a decrease and nothing for a first baseline, a
   * do-nothing week or an increase. The flag is kept apart from `message` on purpose: a user
   * with sparse weigh-ins already has a message there and must still be recognised (#5415).
   * Whether the resulting change is too small to offer is the checkup's decision, in
   * `getProgramUpdateOutcome` (client/src/pages/weekly-checkup/weekly-checkup-outcome.ts).
   */
  private reportDecreaseRaisedToMinimum() {
    let $ = this.internals;
    // if this solve lowered the target, the clamp is undoing a checkup cut
    if ($.adjustmentAction === AdjustmentAction.DecreaseIntakeByFullDeficit
      || $.adjustmentAction === AdjustmentAction.DecreaseIntakeByHalfDeficit
      || $.adjustmentAction === AdjustmentAction.DecreaseIntakeByExcessGain) {
      this.clampedToMinimumIntake = true;
    }
  }

  private findNutritionTargets(analysis: Readonly<WeighInAnalysis>) {
    let $ = this.internals;

    $.weeklyTargets = {};
    $.cutMealTargets = {};
    $.bulkMealTargets = {};
    $.fixedMealTargets = {};

    $.cutMealTargets[INFOODS.Protein] = (1.6 * this.fitnessPlan.nutritionAdjusted.BW_kg) / $.mealsDaily;
    $.cutMealTargets[INFOODS.Calories] = 11 * $.cutMealTargets[INFOODS.Protein];
    $.cutMealTargets[INFOODS.Fiber] = 4;

    this.fitnessPlan.actualDailyAverageIntake = this.fitnessPlan.nominalDailyAverageIntake;

    $.minimumDailyAverageIntake = this.computeMinimumDailyAverageIntake(analysis, this.computeMaintenanceIntake(analysis), this.fitnessPlan.intakeDeltaPercent * 100);
    let minimumDailyAverageIntakeMaxRecommended = this.computeMinimumDailyAverageIntake(analysis, this.computeMaintenanceIntake(analysis), undefined);

    $.cutMealTargets[INFOODS.Calories] = analysis.bodyComposition.factorM * $.cutMealTargets[INFOODS.Protein];
    if ($.minimumDailyAverageIntake < minimumDailyAverageIntakeMaxRecommended) {
      $.cutMealTargets[INFOODS.Calories] *= $.minimumDailyAverageIntake / minimumDailyAverageIntakeMaxRecommended;
    }
    if (this.fitnessPlan.actualDailyAverageIntake < $.minimumDailyAverageIntake) {
      this.reportDecreaseRaisedToMinimum();
      this.fitnessPlan.actualDailyAverageIntake = this.fitnessPlan.nominalDailyAverageIntake = $.minimumDailyAverageIntake;
    }

    if (analysis.estimatedBF_pc) {
      $.weeklyTargets[INFOODS.Protein] = (this.fitnessPlan.nutritionAdjusted.BW_kg + 2) * 1.8 * 7;
      $.weeklyTargets[INFOODS.Calories] = this.fitnessPlan.actualDailyAverageIntake * 7;

      if ($.trainingDaysWeekly > 0) {
        $.bulkMealTargets[INFOODS.Protein] =
          ($.weeklyTargets[INFOODS.Protein] - $.cutMealsPerWeek * $.cutMealTargets[INFOODS.Protein]) / $.bulkMealsPerWeek;
        $.bulkMealTargets[INFOODS.Calories] =
          ($.weeklyTargets[INFOODS.Calories] - $.cutMealsPerWeek * $.cutMealTargets[INFOODS.Calories]) / $.bulkMealsPerWeek;
        $.bulkMealTargets[INFOODS.Fiber] = 6;
      } else {
        $.cutMealTargets = {};
        $.bulkMealTargets = {};
      }

      // populate fixed meal targets from weekly targets (used when calorie cycling is disabled)
      $.fixedMealTargets[INFOODS.Protein] = $.weeklyTargets[INFOODS.Protein] / (7 * $.mealsDaily);
      $.fixedMealTargets[INFOODS.Calories] = $.weeklyTargets[INFOODS.Calories] / (7 * $.mealsDaily);
      $.fixedMealTargets[INFOODS.Fiber] = 6;
    } else {
      $.bulkMealTargets[INFOODS.Protein] = (1.8 * this.fitnessPlan.nutritionAdjusted.BW_kg) / $.mealsDaily;
      $.bulkMealTargets[INFOODS.Calories] = $.bulkMealTargets[INFOODS.Protein] * 11;
      $.bulkMealTargets[INFOODS.Fiber] = 6;

      // populate fixed meal targets from bulk meal targets (used when calorie cycling is disabled)
      $.fixedMealTargets[INFOODS.Protein] = $.bulkMealTargets[INFOODS.Protein];
      $.fixedMealTargets[INFOODS.Calories] = $.bulkMealTargets[INFOODS.Calories];
      $.fixedMealTargets[INFOODS.Fiber] = 6;
    }

    // apply macro overrides (protein and fiber)
    const macroOverrides = this.fitnessPlan.overrideMacros;
    if (macroOverrides) {
      if (macroOverrides.protein_g_per_day != null) {
        const dailyProtein = macroOverrides.protein_g_per_day;
        const proteinPerMeal = dailyProtein / $.mealsDaily;
        $.weeklyTargets[INFOODS.Protein] = dailyProtein * 7;
        for (const targets of [$.bulkMealTargets, $.cutMealTargets, $.fixedMealTargets]) {
          if (targets[INFOODS.Protein] != null) {
            targets[INFOODS.Protein] = proteinPerMeal;
          }
        }
      }
      if (macroOverrides.fiber_g_per_day != null) {
        const fiberPerMeal = macroOverrides.fiber_g_per_day / $.mealsDaily;
        for (const targets of [$.bulkMealTargets, $.cutMealTargets, $.fixedMealTargets]) {
          if (targets[INFOODS.Fiber] != null) {
            targets[INFOODS.Fiber] = fiberPerMeal;
          }
        }
      }
    }

    for (let targets of [$.weeklyTargets, $.bulkMealTargets, $.cutMealTargets, $.fixedMealTargets]) {
      for (let key in targets) {
        targets[key] = Math.round(targets[key]);
      }
    }

    this.fitnessPlan.weeklyTargets = $.weeklyTargets;
    this.fitnessPlan.bulkMealTargets = $.bulkMealTargets;
    this.fitnessPlan.cutMealTargets = $.cutMealTargets;
    this.fitnessPlan.fixedMealTargets = $.fixedMealTargets;
  }

  private generateMeals() {
    let $ = this.internals;
    let isMacrosFixed = this.params.declaredState.macroTargets === MacroTargetsType.Exact;

    if (!this.fitnessPlan.weeklyTargets && !this.fitnessPlan.cutMealTargets && !this.fitnessPlan.bulkMealTargets) {
      return;
    }

    if (isNaN(this.fitnessPlan.nutritionAdjusted.BW_kg)) {
      this.warnings += 'Invalid current frozen weight info\n';
      this.fitnessPlan.nutritionAdjusted.BW_kg = this.params.fitnessState.weight_kg;
    }

    this.findCutBulkMeals();

    $.weeklyTargets = this.fitnessPlan.weeklyTargets;
    $.cutMealTargets = this.fitnessPlan.cutMealTargets;
    $.bulkMealTargets = this.fitnessPlan.bulkMealTargets;
    $.fixedMealTargets = this.fitnessPlan.fixedMealTargets;

    // if calorie cycling is disabled and fixedMealTargets is missing, calculate from weekly targets
    if (!usesCalorieCycling(this.params.declaredState) && !$.fixedMealTargets) {
      $.fixedMealTargets = {
        [INFOODS.Protein]: $.weeklyTargets[INFOODS.Protein] / (7 * $.mealsDaily),
        [INFOODS.Calories]: $.weeklyTargets[INFOODS.Calories] / (7 * $.mealsDaily),
        [INFOODS.Fiber]: 6,
      };
      this.fitnessPlan.fixedMealTargets = $.fixedMealTargets;
    }

    const mealCaloriesMargin = 0.2;
    const dayCaloriesMargin = 0.01;
    const dayMacroMargin = 0.05;

    const mealFatMargin = 0.2;
    const mealCarbsMargin = 0.2;

    for (let day of this.schedule) {
      let dayItem: Schedule_ItemDayStart = null;
      let targetSum = {
        [INFOODS.Protein]: 0,
        [INFOODS.Calories]: 0,
      };

      if (isMacrosFixed) {
        targetSum[INFOODS.Fat] = 0;
        targetSum[INFOODS.CarbNet] = 0;
      }

      let numBulkMeals = 0;

      for (let item of day.items) {
        if (item.type === Schedule_ItemType.Meal) {
          let targets: { [x: string]: number; };
          if ($.trainingDaysWeekly > 0 && $.bulkMealsPerWeek > 0 && usesCalorieCycling(this.params.declaredState)) {
            if (item.cutMeal) {
              targets = $.cutMealTargets;
            } else {
              numBulkMeals++;
              targets = $.bulkMealTargets;
            }
          } else {
            // use fixed meal targets when calorie cycling is not enabled
            targets = $.fixedMealTargets;
          }

          // guard against undefined targets from old plans
          if (!targets) {
            continue;
          }

          item.targets = {
            [INFOODS.Protein]: { min: targets[INFOODS.Protein] },
            [INFOODS.Fiber]: { min: targets[INFOODS.Fiber] },
            [INFOODS.Calories]: {
              target: targets[INFOODS.Calories],
              min: Math.round(targets[INFOODS.Calories] * (1 - mealCaloriesMargin)),
              max: Math.round(targets[INFOODS.Calories] * (1 + mealCaloriesMargin)),
            },
          };

          // if macros are fixed, we take the baseline targets, and adjust them
          // as per https://gitlab.com/cybernetic-fitness/cybernetic-fitness/-/issues/1165
          if (isMacrosFixed) {
            let proteinTarget = targets[INFOODS.Protein];
            let calorieTarget = targets[INFOODS.Calories];
            item.targets[INFOODS.Protein].target = proteinTarget;
            delete item.targets[INFOODS.Fiber];

            let fatTarget: number;
            let carbTarget: number;
            if (this.fitnessPlan.overrideMacros?.carbFatRatio != null) {
              // apply user's carb/fat ratio override
              let dailyCalories = this.fitnessPlan.actualDailyAverageIntake;
              let dailyProtein = targets[INFOODS.Protein] * $.mealsDaily;
              let dailySplit = splitRemainingKcalByCarbRatio(dailyCalories, dailyProtein, this.fitnessPlan.overrideMacros.carbFatRatio);
              carbTarget = dailySplit.carbs_g / $.mealsDaily;
              fatTarget = dailySplit.fat_g / $.mealsDaily;
            } else {
              fatTarget = 0.3 * calorieTarget / KCAL_PER_G_FAT;
              carbTarget = (calorieTarget - fatTarget * KCAL_PER_G_FAT - proteinTarget * KCAL_PER_G_PROTEIN) / KCAL_PER_G_CARBS;
            }

            item.targets[INFOODS.Fat] = {
              target: fatTarget,
              min: Math.round(fatTarget * (1 - mealFatMargin)),
              max: Math.round(fatTarget * (1 + mealFatMargin)),
            };
            item.targets[INFOODS.CarbNet] = {
              target: carbTarget,
              min: Math.round(carbTarget * (1 - mealCarbsMargin)),
              max: Math.round(carbTarget * (1 + mealCarbsMargin)),
            }
            for (let key in targetSum) {
              targetSum[key] += item.targets[key] && item.targets[key].target || 0;
            }

          } else {
            for (let key in targetSum) {
              targetSum[key] += targets[key] || 0;
            }
          }

        } else if (item.type === Schedule_ItemType.DayStart) {
          dayItem = item; // will fill this at the end of the day loop
        }
      }
      if (dayItem === null) {
        continue;
      }

      targetSum[INFOODS.Fiber] = (!usesCalorieCycling(this.params.declaredState) || numBulkMeals > 0) ? ($.gender === Gender.male ? 38 : 25) : $.gender === Gender.male ? 25 : 20;
      // apply fiber override if user customized the daily target
      if (this.fitnessPlan.overrideMacros?.fiber_g_per_day != null) {
        targetSum[INFOODS.Fiber] = this.fitnessPlan.overrideMacros.fiber_g_per_day;
      }
      let BF_pc = this.fitnessPlan.nutritionAdjusted.BF_pc || 30; // until BF is known, we assume 30%
      let LMB_kg = (1 - BF_pc / 100) * this.fitnessPlan.nutritionAdjusted.BW_kg;
      targetSum[INFOODS.Cholesterol] = Math.round(LMB_kg * 7.2)

      if (isMacrosFixed) {

        dayItem.targets = {}
        for (let nutrient in targetSum) {
          let margin = nutrient === INFOODS.Calories ? dayCaloriesMargin : dayMacroMargin;
          dayItem.targets[nutrient] = {
            target: targetSum[nutrient],
            min: Math.round(targetSum[nutrient] * (1 - margin)),
            max: Math.round(targetSum[nutrient] * (1 + margin)),
          }
        }

        dayItem.targets[INFOODS.Fiber] = { min: targetSum[INFOODS.Fiber] };
        dayItem.targets[INFOODS.Cholesterol] = { min: targetSum[INFOODS.Cholesterol] };
        day.items.filter(item => item.type === Schedule_ItemType.Meal).forEach(item => {
          item.targets[INFOODS.Fiber] = { min: targetSum[INFOODS.Fiber] / day.items.filter(i => i.type === Schedule_ItemType.Meal).length };
        });
      } else {

        let dailyFatMin = this.fitnessPlan.overrideMacros?.fat_g_per_day
          ?? getMinimumDailyFat_g(targetSum[INFOODS.Calories]);
        let maxfat_pc = $.gender === Gender.male ? 50 : 55;
        let dailyFatMax = (targetSum[INFOODS.Calories] * maxfat_pc) / 100 / KCAL_PER_G_FAT; // max 50-55% calories from fat
        let carbsBulkMin: number = this.fitnessPlan.overrideMacros?.carbs_g_per_day
          ?? getMinimumDailyCarbs_g($.gender);
        let carbsBulkMax =
          (targetSum[INFOODS.Calories] -
            KCAL_PER_G_PROTEIN * targetSum[INFOODS.Protein] -
            KCAL_PER_G_FIBER * targetSum[INFOODS.Fiber] -
            (targetSum[INFOODS.Calories] * maxfat_pc) / 100) /
          KCAL_PER_G_CARBS;

        dailyFatMin = Math.round(dailyFatMin);
        dailyFatMax = Math.round(dailyFatMax);
        carbsBulkMax = Math.round(carbsBulkMax);
        carbsBulkMin = Math.round(carbsBulkMin);
        if (carbsBulkMin > carbsBulkMax) {
          carbsBulkMax = undefined;
        }

        if (
          targetSum[INFOODS.Calories] <
          KCAL_PER_G_PROTEIN * targetSum[INFOODS.Protein] + KCAL_PER_G_CARBS * carbsBulkMin - KCAL_PER_G_FIBER * targetSum[INFOODS.Fiber] + KCAL_PER_G_FAT * dailyFatMin
        ) {
          carbsBulkMin = carbsBulkMax = undefined;
        }

        dayItem.targets = {
          [INFOODS.Protein]: { min: targetSum[INFOODS.Protein] },
          [INFOODS.Fiber]: { min: targetSum[INFOODS.Fiber] },
          [INFOODS.CarbNet]: numBulkMeals > 0 || carbsBulkMin !== null ? { min: carbsBulkMin } : undefined,
          [INFOODS.Fat]: { min: dailyFatMin },
          [INFOODS.Cholesterol]: { min: targetSum[INFOODS.Cholesterol] },
          [INFOODS.Calories]: {
            target: targetSum[INFOODS.Calories],
            min: Math.round(targetSum[INFOODS.Calories] * (1 - dayCaloriesMargin)),
            max: Math.round(targetSum[INFOODS.Calories] * (1 + dayCaloriesMargin)),
          },
        };
      }
    }

  }

  public checkForPlanAdjustments(params: PlanUpdateParams): CheckForPlanAdjustmentsResult {
    try {
      let $ = this.internals; // for brevity in code below

      this.prepareGeneralParams($, params);
      const analysis = this.runAnalysisStage();
      // the verdict runs even when the plan is marked for a fresh calorie target
      // (shouldInitializeBaselineAtNextReplan). skipping it read as "progress undetermined",
      // which hid the checkup step whose solve clears the mark, so the plan stayed stuck (#4602).
      this.findAdjustmentAction(analysis);

      let errors = '';
      errors += deepCheckValid(this.schedule, 'schedule');
      errors += deepCheckValid(this.internals, 'internals');
      // if weight data was sufficient but fatness data was missing, findAdjustmentAction
      // exited early without setting an action — downgrade message to MeasureMoreOften
      // only when message is AllOk (preserve WeightTooFlat, DataErratic, etc.)
      if ($.adjustmentAction === undefined && this.canAdjustPlan(analysis) && this.message === PlanUpdateUserMessage.AllOk) {
        this.message = PlanUpdateUserMessage.MeasureMoreOften;
      }

      if (errors) {
        return { outcome: PlanUpdateOutcome.Failed, warning: this.warnings, error: errors, internals: this.params.testing ? this.internals : undefined };
      }
      return {
        outcome: PlanUpdateOutcome.Solved,
        warning: this.warnings,
        message: this.message,
        needsAdjustment: $.adjustmentAction && $.adjustmentAction !== AdjustmentAction.DoNothing,
        actionDecided: $.adjustmentAction !== undefined && !this.waitingOnBodyFat,
        // a stored manual target can send a solve with no weight trend into the body-fat
        // branches; there weigh-ins are what is missing, so do not ask for body fat
        needsMoreBodyFatReadings: this.waitingOnBodyFat && analysis.weightSlope_kgwk !== undefined,
        adjustmentReason: $.adjustmentActionReason,
        estimatedBW_kg: analysis.estimatedBW_kg,
        // #5456: numbers the check already worked out, handed back for display only. Nothing
        // above reads them, so they cannot change a decision.
        weightSlope_kgwk: analysis.weightSlope_kgwk,
        fatnessSlopeBottom: analysis.fatnessSlopeBottom,
        fatnessSlopeTop: analysis.fatnessSlopeTop,
        fatnessSeries: analysis.fatnessSeries,
        weightSamples: analysis.weightSamples,
        caliperSamples: analysis.caliperSamples,
        tapeSamples: analysis.tapeSamples,
        firstCountedDayIso: firstCountedDayIso(analysis),
        internals: this.params.testing ? this.internals : undefined,
      };
    } catch (error) {
      console.error(error);
      // the FAILED outcome is what tells a caller this solve crashed, and it carries no answer
      // fields, so a caller cannot read a half-built answer without asking first (#5776).
      //
      // The `||` keeps "a failure always describes itself" true rather than nearly true.
      // `describeError` hands a thrown STRING straight back, so `throw ''` would otherwise come
      // out as `''`, and callers put this message in Sentry and in thrown errors. Reading
      // `error.message` instead was worse: a thrown value that is NOT an Error left it
      // `undefined`. Nothing in here throws a bare string today.
      //
      // `describeError` and not `describeErrorForLog`, which would also carry the stack: this is
      // a field on a result object that callers interpolate into messages and telemetry, and the
      // log-side helper must never reach one. See `design-docs/typescript-patterns.md`. The
      // throw-site stack therefore stays in the `console.error` above and nowhere else. The other
      // two catches in this class follow the same rule.
      return {
        outcome: PlanUpdateOutcome.Failed,
        warning: this.warnings,
        error: describeError(error) || 'PlanUpdate.checkForPlanAdjustments failed with no description',
        // `?.` because `this.params` is assigned by the first statement of `prepareGeneralParams`,
        // so anything that throws before that leaves it unset -- a caller handing over null params
        // is the reachable one. An unguarded read here made the CATCH throw, so the exception
        // escaped the method after all and every guarantee above it was void. The same `?.` is on
        // the other two catches in this class.
        internals: this.params?.testing ? this.internals : undefined,
      };
    }
  }

  public generateSchedule(params: PlanUpdateParams): GenerateScheduleResult {
    try {
      let $ = this.internals; // for brevity in code below

      this.prepareGeneralParams($, params);
      this.generateMeals();

      let errors = '';
      errors += deepCheckValid(this.schedule, 'schedule');
      errors += deepCheckValid(this.internals, 'internals');
      if (errors) {
        return { outcome: PlanUpdateOutcome.Failed, warning: this.warnings, error: errors, unsolvedSchedule: this.schedule, internals: this.params.testing ? this.internals : undefined };
      }
      return {
        outcome: PlanUpdateOutcome.Solved,
        warning: this.warnings,
        schedule: this.schedule,
        internals: this.params.testing ? this.internals : undefined,
      };
    } catch (error) {
      console.error(error);
      // never empty, and never `undefined` for a non-Error throw -- see the catch in
      // `checkForPlanAdjustments` above for why both halves of that matter.
      return {
        outcome: PlanUpdateOutcome.Failed,
        warning: this.warnings,
        error: describeError(error) || 'PlanUpdate.generateSchedule failed with no description',
        unsolvedSchedule: params?.schedule,
        internals: this.params?.testing ? this.internals : undefined,
      };
    }
  }

  public solve(params: PlanUpdateParams): PlanUpdateResults {
    try {
      let $ = this.internals; // for brevity in code below

      this.prepareGeneralParams($, params);

      // before anything reads the parked calorie request, drop one that is not a target
      // anybody could eat to. see discardUnusableIntakeRequest.
      this.discardUnusableIntakeRequest();

      const analysis = this.runAnalysisStage();
      if (analysis.bodyComposition) {
        this.fitnessPlan.bodyCompositionType = analysis.bodyComposition.type;
      }

      // if program is not _completely_ (*) initialized yet
      // (*) Program is completely initialized only after we have a BF_pc, which is only on the next update for the photo-based BF case.
      if (!this.isBaselineComplete() || this.fitnessPlan.shouldInitializeBaselineAtNextReplan) {
        this.initializeBaseline(analysis);
        // if program is already complete and we are now just doing adjustments
      } else if (!this.fitnessPlan.skipNextNutritionAdjustment) {
        this.adjustForProgress(analysis);
      }
      this.fitnessPlan.skipNextNutritionAdjustment = undefined;

      // findNutritionTargets multiplies nutritionAdjusted.BW_kg into meal targets;
      // if estimatedBW_kg is undefined (LogWeighInNeeded path) the products are NaN
      // and deepCheckValid below would fail the whole solve. skip the targets pass.
      if (analysis.estimatedBF_pc !== undefined && analysis.estimatedBW_kg !== undefined) {
        // a daily calorie target the user typed is only put into force by the
        // ManualOverride case of adjustForProgress, and the branch above does not always
        // reach it — a plan flagged for a nutrition rebuild, a baseline that is not
        // complete yet, or a skipped adjustment all take another path. The typed number
        // was then silently dropped while the screens went on showing it as if it were
        // in force (#5181). Apply it here so every path ends the same way, and just
        // before findNutritionTargets so the day's meal targets are built from it.
        //
        // This sits INSIDE the body-data check on purpose. Without a bodyweight or a
        // body-fat reading the solve cannot rebuild the meal targets, so putting a typed
        // target into force here would leave the plan's target and its meal targets
        // describing different numbers, and would consume the request while doing it.
        // Leaving it parked instead means the next solve applies it in full.
        if (this.applyPendingIntakeOverride()) {
          // the target in force just changed, so the anchor that says when it last
          // changed, and against which body state, has to move with it. The two branches
          // above already do this for the targets THEY compute; the routes that reach
          // here without having computed one (a skipped adjustment, or adjustForProgress
          // returning early) would otherwise measure the next solve's progress from a
          // point before the user's own number was in force. Direction 0 is what the
          // ManualOverride case stores too: a typed target has no direction of its own,
          // and it must not trip the opposite-direction damper in findAdjustmentAction.
          this.fitnessPlan.nutritionAdjusted = {
            timestamp_utc: this.today_utc.toISO(),
            BW_kg: analysis.estimatedBW_kg,
            BF_pc: analysis.estimatedBF_pc,
            lastAdjustmentDirection: 0,
          };
          this.fitnessPlan.needsReadjustment = undefined;
        }
        this.findNutritionTargets(analysis);
      }

      let errors = '';
      errors += deepCheckValid(this.schedule, 'schedule');
      errors += deepCheckValid(this.fitnessPlan, 'fitnessPlan');
      errors += deepCheckValid(this.internals, 'internals');
      if (errors) {
        return { outcome: PlanUpdateOutcome.Failed, warning: this.warnings, error: errors, internals: this.params.testing ? this.internals : undefined };
      }
      return {
        outcome: PlanUpdateOutcome.Solved,
        warning: this.warnings,
        message: this.message,
        fitnessPlan: this.fitnessPlan,
        healedIntakeDelta: this.healedIntakeDelta,
        clampedToMinimumIntake: this.clampedToMinimumIntake,
        minimumDailyAverageIntake: this.internals.minimumDailyAverageIntake,
        internals: this.params.testing ? this.internals : undefined,
      };
    } catch (error) {
      console.error(error);
      // never empty, and never `undefined` for a non-Error throw -- see the catch in
      // `checkForPlanAdjustments` above for why both halves of that matter.
      return {
        outcome: PlanUpdateOutcome.Failed,
        warning: this.warnings,
        error: describeError(error) || 'PlanUpdate.solve failed with no description',
        internals: this.params?.testing ? this.internals : undefined,
      };
    }
  }
}
